import React from 'react';
import { animated } from '@react-spring/web';

import Modal from '~/components/modals/modal';
import SmallLawnModalForm from '~/components/modals/small-lawn/small-lawn-modal-form';
import SeeWhy from '~/components/modals/small-lawn/see-why';
import { useLawnMutations } from '~/features/lawns/lawn-actions';
import { lawnIssue } from '~/features/lawns/lawn';
import { useFlashMessage } from '~/features/flash-messages/flash-message-actions';
import { useAnimatedPageTransitions } from '~/hooks/use-animated-page-transitions';
import { useFunnelRequireAuth } from '~/hooks/use-funnel-require-auth';
import { captureException } from '~/utils/exception-tracking';

export const pages = {
  INITIAL: 0,
  SEE_WHY: 1,
};

const SmallLawnModal = ({ isOpen, onClose, lawn, newLawnSize, onContinue }) => {
  const { showFlashMessage } = useFlashMessage();
  const { updateLawn } = useLawnMutations();
  const { requireAuth } = useFunnelRequireAuth({ onModalClose: onClose });

  const { transitionTo, transitions, resetPage } = useAnimatedPageTransitions(
    pages.INITIAL
  );

  const handleClose = () => {
    resetPage();
    onClose();
  };

  const doUpdateLawn = async (lawnData) => {
    await requireAuth();

    try {
      await updateLawn(lawn.uuid, lawnData);
      if (onContinue) {
        await onContinue();
      }
      handleClose();
    } catch (err) {
      showFlashMessage('There was a problem saving your response', 'error');
      captureException(err, {
        fingerprints: ['{{ default }}', 'SmallLawnModal'],
        extras: {
          location: '<SmallLawnModal />',
        },
      });
    }
  };

  const requestOrderAnyway = () => {
    return doUpdateLawn({
      lawnIssueSelection: lawnIssue.ORDER_ANYWAY,
      lawnIssueMessage: '',
    });
  };

  const handleRadioSelection = async ({ type, payload }) => {
    switch (type) {
      case 'navigate':
        return transitionTo(payload, 1);
      case 'closeModal':
        return handleClose();
      case 'requestOrderAnyway':
        return requestOrderAnyway().then(handleClose);
      default:
        return null;
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      {transitions((styleProps, item) => {
        switch (Number(item)) {
          case pages.INITIAL:
            return (
              <animated.div style={styleProps}>
                <SmallLawnModalForm
                  onSubmit={handleRadioSelection}
                  lawnSqFt={newLawnSize || lawn?.size.sqFt}
                  smallLawnThreshold={lawn?.size?.smallLawnThreshold}
                  onClose={onClose}
                />
              </animated.div>
            );
          case pages.SEE_WHY:
            return (
              <animated.div style={styleProps}>
                <SeeWhy
                  onCancel={() => transitionTo(pages.INITIAL, -1)}
                  smallLawnThreshold={lawn?.size?.smallLawnThreshold}
                />
              </animated.div>
            );
          default:
            return null;
        }
      })}
    </Modal>
  );
};

export default SmallLawnModal;
